import { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Landmark, ExternalLink, CalendarClock, Loader2, FileText, HardHat, X, BarChart3 } from "lucide-react";
import api from "../lib/api";
import { GovReport } from "./GovReport";
import { BidNoBid } from "./BidNoBid";

const STATUSES = ["WATCHING", "PREPARING", "SUBMITTED", "WON", "LOST"];
const COL_STYLE = {
  WATCHING: "border-t-slate-400", PREPARING: "border-t-blue-500", SUBMITTED: "border-t-amber-500",
  WON: "border-t-green-600", LOST: "border-t-red-400",
};
const BADGE = {
  WATCHING: "bg-slate-100 text-slate-600", PREPARING: "bg-blue-100 text-blue-700",
  SUBMITTED: "bg-amber-100 text-amber-700", WON: "bg-green-100 text-green-700", LOST: "bg-red-100 text-red-600",
};
const money = (v) => (v || v === 0) ? `$${Number(v).toLocaleString("en-US", { maximumFractionDigits: 0 })}` : "";

function dueChip(opp) {
  if (!opp.due_date || ["WON", "LOST"].includes(opp.status)) return null;
  const days = Math.ceil((new Date(opp.due_date + "T23:59:59") - new Date()) / 86400000);
  const short = new Date(opp.due_date + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" });
  if (days < 0) return <span className="shrink-0 px-2 py-0.5 rounded-full text-[11px] font-[Poppins] font-semibold bg-red-100 text-red-600">Overdue</span>;
  if (days <= 7) return <span className="shrink-0 px-2 py-0.5 rounded-full text-[11px] font-[Poppins] font-semibold bg-amber-100 text-amber-700">Due in {days}d</span>;
  return <span className="shrink-0 px-2 py-0.5 rounded-full text-[11px] font-[Poppins] font-semibold bg-slate-100 text-slate-600">Due {short}</span>;
}

export default function Government() {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [contractors, setContractors] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creatingRfq, setCreatingRfq] = useState(null);
  const [view, setView] = useState("board");

  const load = useCallback(() => {
    api.get("/admin/gov-opps").then((r) => setItems(r.data)).catch(() => setItems([]));
    api.get("/admin/contractors").then((r) => setContractors(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const updateTeam = async (opp, team) => {
    try { await api.patch(`/admin/gov-opps/${opp.id}`, { team }); load(); }
    catch { toast.error("Could not update the team"); }
  };

  const createRfq = async (opp) => {
    setCreatingRfq(opp.id);
    try {
      const { data } = await api.post(`/admin/gov-opps/${opp.id}/create-rfq`);
      toast.success(data.created ? `RFQ ${data.rfq_number} created — sourcing started` : `Opening existing ${data.rfq_number}`);
      navigate(`/admin/rfqs/${data.rfq_id}`);
    } catch { toast.error("Could not create RFQ"); }
    finally { setCreatingRfq(null); }
  };

  const move = async (opp, status) => {
    try { await api.patch(`/admin/gov-opps/${opp.id}`, { status }); toast.success(`Moved to ${status}`); load(); }
    catch { toast.error("Update failed"); }
  };
  const remove = async (opp) => {
    if (!window.confirm(`Delete "${opp.title}"?`)) return;
    try { await api.delete(`/admin/gov-opps/${opp.id}`); toast.success("Opportunity deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  const won = (items || []).filter((o) => o.status === "WON").length;
  const decided = won + (items || []).filter((o) => o.status === "LOST").length;

  return (
    <div data-testid="admin-gov-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-[Poppins] font-bold text-[26px] text-[var(--pg-navy)]">Government Bids</h1>
        <div className="flex items-center gap-2">
          <div className="flex rounded-lg border border-slate-200 overflow-hidden">
            <button data-testid="gov-view-board" onClick={() => setView("board")}
              className={`px-3.5 py-2.5 text-[12.5px] font-[Poppins] font-semibold ${view === "board" ? "bg-[var(--pg-navy)] text-white" : "bg-white text-slate-500 hover:text-[var(--pg-navy)]"}`}>Board</button>
            <button data-testid="gov-view-report" onClick={() => setView("report")}
              className={`px-3.5 py-2.5 text-[12.5px] font-[Poppins] font-semibold inline-flex items-center gap-1.5 ${view === "report" ? "bg-[var(--pg-navy)] text-white" : "bg-white text-slate-500 hover:text-[var(--pg-navy)]"}`}><BarChart3 size={13} /> Report</button>
          </div>
          <button data-testid="add-gov-opp-button" onClick={() => setEditing({})} className="btn-primary !py-2.5 !px-4 text-[13px]"><Plus size={15} /> Add Opportunity</button>
        </div>
      </div>
      <p className="mt-2 text-[13.5px] text-slate-500">
        Track government solicitations from watch to award.
        {decided > 0 && <span className="ml-2 font-[Poppins] font-semibold text-[var(--pg-navy)]" data-testid="gov-win-rate">Win rate: {Math.round((won / decided) * 100)}% ({won}/{decided})</span>}
      </p>

      {editing && <OppForm opp={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}

      {view === "report" ? (
        <GovReport />
      ) : items === null ? (
        <div className="mt-10 text-center text-slate-400"><Loader2 className="animate-spin inline" size={22} /></div>
      ) : (
        <div className="mt-5 grid grid-cols-1 md:grid-cols-3 xl:grid-cols-5 gap-4">
          {STATUSES.map((s) => {
            const col = items.filter((o) => o.status === s);
            return (
              <div key={s} data-testid={`gov-column-${s}`} className={`bg-white rounded-xl border border-slate-200 border-t-4 ${COL_STYLE[s]} p-3 min-h-[160px]`}>
                <div className="flex items-center justify-between px-1">
                  <span className="font-[Poppins] font-bold text-[12px] tracking-wider text-[var(--pg-navy)]">{s}</span>
                  <span className={`px-2 py-0.5 rounded-full text-[10.5px] font-[Poppins] font-bold ${BADGE[s]}`}>{col.length}</span>
                </div>
                <div className="mt-3 space-y-3">
                  {col.length === 0 && <p className="text-[12px] text-slate-300 text-center py-4">—</p>}
                  {col.map((o) => (
                    <div key={o.id} data-testid={`gov-card-${o.id}`} className="rounded-lg border border-slate-200 bg-[var(--pg-bg)] p-3 hover:border-[var(--pg-accent)] transition-colors">
                      <div className="flex flex-wrap items-start justify-between gap-x-2 gap-y-1">
                        <p className="font-[Poppins] font-semibold text-[13px] text-[var(--pg-navy)] leading-snug flex-1 min-w-[140px]">{o.title}</p>
                        <div className="flex items-center gap-1 flex-shrink-0 max-w-full flex-wrap">
                          <BidNoBid opp={o} onSaved={load} />
                          {dueChip(o)}
                        </div>
                      </div>
                      <p className="mt-1 text-[12px] text-slate-500 flex items-center gap-1"><Landmark size={12} /> {o.agency}{o.solicitation_no ? ` · ${o.solicitation_no}` : ""}</p>
                      <div className="mt-1.5 flex items-center gap-2 text-[12px] text-slate-500">
                        {(o.est_value || o.est_value === 0) && <span className="font-semibold text-[var(--pg-navy)]">{money(o.est_value)}</span>}
                        {o.due_date && <span className="inline-flex items-center gap-1"><CalendarClock size={12} /> {o.due_date}</span>}
                      </div>
                      {o.notes && <p className="mt-1.5 text-[12px] text-slate-500 line-clamp-3 whitespace-pre-line">{o.notes}</p>}
                      <TeamRow opp={o} contractors={contractors} onUpdate={updateTeam} />
                      <div className="mt-2.5">
                        {o.rfq_id ? (
                          <Link data-testid={`gov-view-rfq-${o.id}`} to={`/admin/rfqs/${o.rfq_id}`}
                            className="inline-flex items-center gap-1.5 text-[11.5px] font-[Poppins] font-semibold px-3 py-1.5 rounded-lg bg-blue-50 text-blue-700 border border-blue-100 hover:bg-blue-100">
                            <FileText size={12} /> View RFQ
                          </Link>
                        ) : (
                          <button data-testid={`gov-create-rfq-${o.id}`} onClick={() => createRfq(o)} disabled={creatingRfq === o.id}
                            className="inline-flex items-center gap-1.5 text-[11.5px] font-[Poppins] font-semibold px-3 py-1.5 rounded-lg bg-[var(--pg-navy)] text-white hover:bg-[var(--pg-accent)] transition-colors disabled:opacity-70">
                            {creatingRfq === o.id ? <Loader2 className="animate-spin" size={12} /> : <FileText size={12} />} Create RFQ
                          </button>
                        )}
                      </div>
                      <div className="mt-2.5 flex items-center gap-1.5">
                        <select data-testid={`gov-status-select-${o.id}`} className="pg-input !py-1 !px-1.5 !w-auto text-[11.5px] flex-1" value={o.status} onChange={(e) => move(o, e.target.value)}>
                          {STATUSES.map((x) => <option key={x} value={x}>{x}</option>)}
                        </select>
                        {o.url && <a data-testid={`gov-link-${o.id}`} href={o.url} target="_blank" rel="noreferrer" title="Open solicitation" className="text-slate-500 hover:text-[var(--pg-accent)] p-1"><ExternalLink size={14} /></a>}
                        <button data-testid={`gov-edit-${o.id}`} onClick={() => setEditing(o)} title="Edit" className="text-slate-500 hover:text-[var(--pg-navy)] p-1"><Pencil size={14} /></button>
                        <button data-testid={`gov-delete-${o.id}`} onClick={() => remove(o)} title="Delete" className="text-slate-500 hover:text-red-600 p-1"><Trash2 size={14} /></button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function TeamRow({ opp, contractors, onUpdate }) {
  const team = opp.team || [];
  const byId = Object.fromEntries(contractors.map((c) => [c.id, c]));
  const members = team.filter((id) => byId[id]);
  const available = contractors.filter((c) => !team.includes(c.id));
  return (
    <div className="mt-2.5" data-testid={`gov-team-${opp.id}`}>
      <div className="flex items-center gap-1.5 flex-wrap">
        <HardHat size={12} className="text-slate-400 shrink-0" />
        {members.length === 0 && <span className="text-[11.5px] text-slate-400">No team yet</span>}
        {members.map((id) => (
          <span key={id} data-testid={`gov-team-chip-${opp.id}-${id}`}
            className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full text-[11px] font-[Poppins] font-semibold bg-slate-100 text-[var(--pg-navy)] border border-slate-200">
            {byId[id].company_name}
            <span className={`px-1 rounded text-[9px] ${byId[id].contractor_type === "PRIME" ? "bg-[var(--pg-navy)] text-white" : "bg-slate-300 text-slate-600"}`}>
              {byId[id].contractor_type === "PRIME" ? "P" : "S"}
            </span>
            <button data-testid={`gov-team-remove-${opp.id}-${id}`} onClick={() => onUpdate(opp, team.filter((t) => t !== id))}
              className="text-slate-400 hover:text-red-600 px-0.5" aria-label={`Remove ${byId[id].company_name}`}><X size={11} /></button>
          </span>
        ))}
        {available.length > 0 && (
          <select data-testid={`gov-team-add-${opp.id}`} value=""
            onChange={(e) => e.target.value && onUpdate(opp, [...team, e.target.value])}
            className="text-[11px] font-[Poppins] font-semibold text-[var(--pg-accent)] bg-transparent border border-dashed border-slate-300 rounded-full px-2 py-0.5 cursor-pointer hover:border-[var(--pg-accent)] w-[90px]">
            <option value="">+ Team</option>
            {available.map((c) => <option key={c.id} value={c.id}>{c.company_name} ({c.contractor_type === "PRIME" ? "Prime" : "Sub"})</option>)}
          </select>
        )}
      </div>
    </div>
  );
}

function OppForm({ opp, onClose, onSaved }) {
  const isEdit = Boolean(opp.id);
  const [f, setF] = useState({
    title: opp.title || "", agency: opp.agency || "", solicitation_no: opp.solicitation_no || "",
    due_date: opp.due_date || "", est_value: opp.est_value ?? "", url: opp.url || "",
    notes: opp.notes || "", status: opp.status || "WATCHING",
  });
  const [busy, setBusy] = useState(false);
  const h = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = { ...f, est_value: f.est_value === "" ? null : Number(f.est_value) };
    try {
      if (isEdit) await api.patch(`/admin/gov-opps/${opp.id}`, payload);
      else await api.post("/admin/gov-opps", payload);
      toast.success(isEdit ? "Opportunity updated" : "Opportunity added");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center px-4 overflow-y-auto py-8" onClick={onClose}>
      <form data-testid="gov-opp-form" onSubmit={submit} onClick={(e) => e.stopPropagation()} className="w-full max-w-lg bg-white rounded-2xl p-8 shadow-2xl">
        <h2 className="font-[Poppins] font-bold text-[19px] text-[var(--pg-navy)]">{isEdit ? "Edit Opportunity" : "Add Opportunity"}</h2>
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2"><label className="pg-label">Title *</label><input data-testid="gov-title-input" className="pg-input" value={f.title} onChange={h("title")} required minLength={2} /></div>
          <div><label className="pg-label">Agency *</label><input data-testid="gov-agency-input" className="pg-input" value={f.agency} onChange={h("agency")} required minLength={2} /></div>
          <div><label className="pg-label">Solicitation #</label><input data-testid="gov-sol-input" className="pg-input" value={f.solicitation_no} onChange={h("solicitation_no")} /></div>
          <div><label className="pg-label">Deadline</label><input data-testid="gov-due-input" type="date" className="pg-input" value={f.due_date} onChange={h("due_date")} /></div>
          <div><label className="pg-label">Est. Value ($)</label><input data-testid="gov-value-input" type="number" min="0" step="any" className="pg-input" value={f.est_value} onChange={h("est_value")} /></div>
          <div className="sm:col-span-2"><label className="pg-label">Solicitation Link</label><input data-testid="gov-url-input" type="url" className="pg-input" placeholder="https://sam.gov/..." value={f.url} onChange={h("url")} /></div>
          <div><label className="pg-label">Status</label>
            <select data-testid="gov-status-input" className="pg-input" value={f.status} onChange={h("status")}>
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select></div>
          <div className="sm:col-span-2"><label className="pg-label">Notes <span className="text-slate-400 font-normal">(strategy, win/loss reasons)</span></label>
            <textarea data-testid="gov-notes-input" rows={3} className="pg-input" value={f.notes} onChange={h("notes")} /></div>
        </div>
        <div className="mt-6 flex gap-3">
          <button data-testid="gov-form-submit" type="submit" disabled={busy} className="btn-primary flex-1 justify-center disabled:opacity-70">
            {busy ? <Loader2 className="animate-spin" size={17} /> : isEdit ? "Save Changes" : "Add Opportunity"}
          </button>
          <button type="button" data-testid="gov-form-cancel" onClick={onClose} className="px-5 rounded-lg border border-slate-200 text-[13.5px] font-[Poppins] font-semibold text-slate-500 hover:border-slate-400">Cancel</button>
        </div>
      </form>
    </div>
  );
}
